// THE DEMO-DATA GUARD (KyckConnect demo data, 2026-11-02). ONE predicate every outbound path asks before it acts.
//
// ⚠️⚠️ PER DOC, NEVER PER ACCOUNT. `Account.demoAccount` marks which club may be SEEDED; it must never block anything: the demo
// club is also a live test club, and real test leads, calls and texts there must work exactly as they do on any other club.
// What is refused is a doc that carries `demoSeed`, or (on the SMS / call doors only) a phone inside ACMA's fiction ranges.
//
// ⚠️ The relay is the one place the phone rule is NOT used: the relay's own "Send test lead" legitimately carries a fiction
// mobile (TEST_LEAD_PHONE) to the dealer's form. The relay refuses on `demoSeed` alone.
//
// RESERVED FICTION NUMBERS: https://www.acma.gov.au/phone-numbers-use-tv-shows-films-and-creative-works (read 2026-10-26).
//   Landlines: (02) 5550 xxxx and (02) 7010 xxxx; the same two blocks for (03), (07) and (08).
//   Mobiles:   30 individual numbers, all 0491 57x xxx, listed below (a mobile "block" does not exist; only these 30).
// A number outside these is never generated and never treated as demo.

export const FICTION_LANDLINE_AREA_CODES = ['2', '3', '7', '8'] as const;
export const FICTION_LANDLINE_BLOCKS = ['5550', '7010'] as const;

/** The 30 reserved fiction mobiles, in national format without spaces. */
export const FICTION_MOBILES: readonly string[] = [
  '0491570006', '0491570156', '0491570157', '0491570158', '0491570159', '0491570110', '0491570313', '0491570737',
  '0491571266', '0491571491', '0491571804', '0491572549', '0491572665', '0491572983', '0491573770', '0491573087',
  '0491574118', '0491574632', '0491575254', '0491575789', '0491576398', '0491576801', '0491577426', '0491577644',
  '0491578957', '0491578148', '0491578888', '0491579212', '0491579760', '0491579455',
];

const LANDLINE_RE = new RegExp(`^0[${FICTION_LANDLINE_AREA_CODES.join('')}](${FICTION_LANDLINE_BLOCKS.join('|')})\\d{4}$`);
const MOBILE_SET = new Set(FICTION_MOBILES);

/** National digits ("0255501234") from any common Australian shape ("+61 2 5550 1234", "02 5550 1234"); null when not AU-shaped. */
function nationalDigits(phone: string | null | undefined): string | null {
  if (!phone) return null;
  const d = phone.replace(/[^0-9]/g, '');
  if (d.startsWith('61') && d.length >= 11) return `0${d.slice(2)}`;
  if (d.startsWith('0')) return d;
  return null;
}

/** True only for a number inside ACMA's fiction ranges. Anything unreadable, foreign or real is false. */
export function isFictionNumber(phone: string | null | undefined): boolean {
  const n = nationalDigits(phone);
  if (!n) return false;
  return LANDLINE_RE.test(n) || MOBILE_SET.has(n);
}

/** E.164 for a national fiction number ("0255501234" -> "+61255501234"). */
export function fictionToE164(national: string): string {
  return `+61${national.replace(/^0/, '')}`;
}

export interface DemoCheckable {
  /** The run id stamped on every seeded doc. */
  demoSeed?: string | null;
  /** The lead's phone, when the caller has one (SMS and call doors). */
  phone?: string | null;
}

/**
 * Is this doc (or number) demo data that no outbound path may act on? `demoSeed` on the doc, or a fiction phone.
 * Pass `usePhone: false` for the relay (see the header).
 */
export function isDemoData(input: DemoCheckable, opts: { usePhone?: boolean } = {}): boolean {
  if (typeof input.demoSeed === 'string' && input.demoSeed !== '') return true;
  if (opts.usePhone === false) return false;
  return isFictionNumber(input.phone);
}

/**
 * The one count-only log line a refusal prints. ⚠️ No name, number, URL or id of a lead: a label and nothing else.
 * Returns the line so a caller (and a test) can see exactly what was logged.
 */
export function demoRefusalLine(door: string): string {
  return `[demo-data] refused ${door}: demo data is never acted on`;
}

export function logDemoRefusal(door: string): void {
  console.info(demoRefusalLine(door));
}

/**
 * Staff queues and cross-club lists never show demo docs (Approvals, the Lead Bot queue, the all-clients report data). PURE.
 * ⚠️ Per DOC: a real test conversation on the demo club is NOT removed, only a doc that carries `demoSeed`.
 */
export function withoutDemo<T extends { demoSeed?: string | null }>(list: readonly T[]): T[] {
  return list.filter((d) => !(typeof d.demoSeed === 'string' && d.demoSeed !== ''));
}

/**
 * May the UI render this lead's number as a `tel:` link? Not for demo data: the number is shown as plain text, so a click on a demo
 * lead can never dial. A real lead (even on the demo club) keeps its link.
 */
export function canDialLead(lead: { demo?: boolean | null; phone: string }): boolean {
  return !isDemoData({ demoSeed: lead.demo ? 'demo' : null, phone: lead.phone });
}
